import styled, { css } from 'styled-components';
import { E as EXITED } from '../types/react-transition-group/Transition-QJ5gMye0.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { a as paddingMixinWithSafeArea, b as mobilePaddingMixinWithSafeArea } from '../theme/style-mixins/padding/index-BPYuRWh5.js';
import { i as invertedFocusOutline } from '../theme/style-mixins/FocusIndicator/invertedFocusOutline-BxN_kWq6.js';
import { B as Button } from '../Button/Button-BzSULnj6.js';

const Shroud = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-0"
})(["background-color:", ";backdrop-filter:blur(", ");position:fixed;top:0;right:auto;bottom:auto;left:0;height:100%;width:100vw;display:flex;align-items:center;justify-content:center;z-index:", ";@media (prefers-reduced-motion:no-preference){transition:", ";}opacity:0;opacity:", ";", ""], ({
  theme
}) => theme.modal.shroudColor, ({
  theme
}) => theme.modal.shroudBlur, ({
  $zIndex
}) => $zIndex, ({
  $transitionDuration
}) => `opacity ${$transitionDuration}ms ease-in-out`, ({
  $fadeState
}) => ({
  entering: 0,
  entered: 1,
  exiting: 0,
  exited: 0,
  unmounted: 0
})[$fadeState], ({
  $fadeState
}) => $fadeState === EXITED && `
  visibility: hidden;
  `);
Shroud.displayName = 'Shroud';
const transformLeftToRight = {
  entering: 'translateX(-100%)',
  entered: 'translateX(0%)',
  exiting: 'translateX(-100%)',
  exited: 'translateX(0%)',
  unmounted: 'translateX(0%)'
};
const transformRightToLeft = {
  entering: 'translateX(100%)',
  entered: 'translateX(0%)',
  exiting: 'translateX(100%)',
  exited: 'translateX(0%)',
  unmounted: 'translateX(0%)'
};
const transformBottomToTop = {
  entering: 'translateY(100%)',
  entered: 'translateY(0%)',
  exiting: 'translateY(100%)',
  exited: 'translateY(0%)',
  unmounted: 'translateY(0%)'
};
const getSheetContentContainerStyle = (side, $slideState) => {
  switch (side) {
    case 'start':
      return css(["max-height:100%;height:100%;bottom:0;left:0;inset-inline-start:0;right:auto;inset-inline-end:auto;/*! @noflip */ transform:", ";display:flex;flex-direction:row-reverse;/*! @noflip */ [dir=\"rtl\"] &&{/*! @noflip */ transform:", ";right:0;inset-inline-start:0;left:auto;inset-inline-end:auto;}"], transformLeftToRight[$slideState], transformRightToLeft[$slideState]);
    case 'end':
      return css(["max-height:100%;height:100%;bottom:0;left:auto;inset-inline-start:auto;right:0;inset-inline-end:0;/*! @noflip */ transform:", ";display:flex;flex-direction:row;/*! @noflip */ [dir=\"rtl\"] &&{/*! @noflip */ transform:", ";right:auto;inset-inline-start:auto;left:0;inset-inline-end:0;}"], transformRightToLeft[$slideState], transformLeftToRight[$slideState]);
    case 'bottom':
    default:
      return css(["max-width:100vw;width:100vw;bottom:0;left:0;/*! @noflip */ transform:", ""], transformBottomToTop[$slideState]);
  }
};
const SheetContentContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-1"
})(["border-radius:0;box-sizing:border-box;word-wrap:break-word;outline:none;position:absolute;", ";@media (prefers-reduced-motion:no-preference){transition:", ";}"], ({
  $side,
  $slideState
}) => getSheetContentContainerStyle($side, $slideState), ({
  $transitionDuration
}) => `transform ${$transitionDuration}ms ease-in-out`);
SheetContentContainer.displayName = 'SheetContentContainer';
const getPaddingValues = padding => {
  if (!padding) {
    return {
      block: 'none',
      inlineStart: 'none',
      inlineEnd: 'none'
    };
  }
  const values = padding.split(' ');
  // For single value, use it for both block and inline
  if (values.length === 1) {
    return {
      block: values[0],
      inlineStart: values[0],
      inlineEnd: values[0]
    };
  }

  // For two values: first is block (top/bottom), second is inline (left/right)
  if (values.length === 2) {
    return {
      block: values[0],
      inlineStart: values[1],
      inlineEnd: values[1]
    };
  }

  // For three values: first is top, second is inline (left/right), third is bottom
  if (values.length === 3) {
    return {
      block: values[2],
      // Use bottom padding
      inlineStart: values[1],
      inlineEnd: values[1]
    };
  }

  // For four values: top, right, bottom, left
  return {
    block: values[2],
    // Use bottom padding
    inlineStart: values[3],
    inlineEnd: values[1]
  };
};
const getSheetContentStyle = ({
  side,
  $maxSize,
  padding,
  theme,
  isMobile = false,
  $extraSpace,
  $transitionDuration
}) => {
  const paddingValues = getPaddingValues(padding);
  const platform = isMobile ? 'default' : 'mobile';
  switch (side) {
    case 'start':
    case 'end':
      {
        var _theme$spacing$paddin, _theme$spacing$paddin2;
        const inlineStart = ((_theme$spacing$paddin = theme.spacing[paddingValues.inlineStart]) === null || _theme$spacing$paddin === void 0 ? void 0 : _theme$spacing$paddin[platform]) || 0;
        const inlineEnd = ((_theme$spacing$paddin2 = theme.spacing[paddingValues.inlineEnd]) === null || _theme$spacing$paddin2 === void 0 ? void 0 : _theme$spacing$paddin2[platform]) || 0;
        const extraSpace = $extraSpace !== null && $extraSpace !== void 0 ? $extraSpace : '0px';
        return css(["box-sizing:border-box;transition-property:max-width,padding-inline;transition-duration:", "ms;transition-timing-function:ease-in-out;", ":", ";", ":1px solid ", ";max-width:", ";height:100%;"], $transitionDuration, side === 'start' ? 'padding-inline-start' : 'padding-inline-end', extraSpace, side === 'start' ? 'border-inline-end' : 'border-inline-start', theme.elevation.borderColor.subtle, `min(
          100vw,
          calc(
            ${$maxSize === 'viewport' ? '100vw' : '80vw'}
            + ${extraSpace}
            + max(${inlineStart}, env(safe-area-inset-left))
            + max(${inlineEnd}, env(safe-area-inset-right))
          )
        )`);
      }
    case 'bottom':
    default:
      {
        var _theme$spacing$paddin3;
        const blockPadding = ((_theme$spacing$paddin3 = theme.spacing[paddingValues.block]) === null || _theme$spacing$paddin3 === void 0 ? void 0 : _theme$spacing$paddin3[platform]) || 0;
        const extraSpace = $extraSpace !== null && $extraSpace !== void 0 ? $extraSpace : '0px';
        return css(["box-sizing:border-box;transition-property:max-height,padding-block;transition-duration:", "ms;transition-timing-function:ease-in-out;padding-block-end:", ";border-block-start:1px solid ", ";max-height:", ";", ""], $transitionDuration, extraSpace, theme.elevation.borderColor.subtle, `min(
          100vh,
          calc(
            ${$maxSize === 'viewport' ? '100vh' : '65vh'}
            + ${extraSpace}
            + ${blockPadding}
            + max(${blockPadding}, env(safe-area-inset-bottom))
          )
        )`, /* iOS workaround */$maxSize === 'viewport' ? 'max-height: -webkit-fill-available;' : '');
      }
  }
};
const SheetContentSurface = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-2"
})(["background-color:", ";box-shadow:", ";", " ", ";", ";overflow-y:auto;pointer-events:auto;"], ({
  theme
}) => theme.surface.default, ({
  theme
}) => theme.elevation.shadow.modal, ({
  $side
}) => $side === 'bottom' && css(["box-shadow:", ";"], ({
  theme
}) => theme.elevation.shadow.bottomSheet), ({
  theme,
  $side,
  $maxSize,
  $padding,
  $transitionDuration,
  $extraSpace
}) => getSheetContentStyle({
  side: $side,
  $maxSize,
  padding: $padding,
  theme,
  $transitionDuration,
  $extraSpace
}), ({
  theme,
  $side,
  $maxSize,
  $padding,
  $transitionDuration,
  $extraSpace
}) => isMobile(getSheetContentStyle({
  side: $side,
  $maxSize,
  padding: $padding,
  theme,
  isMobile: true,
  $transitionDuration,
  $extraSpace
})));
SheetContentSurface.displayName = 'SheetContentSurface';
// To make sure that SheetHeader is over SheetContent.
const SHEET_CONTENT_Z_INDEX = 0;
const SheetContent = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-3"
})(["z-index:", ";position:relative;", " ", ""], SHEET_CONTENT_Z_INDEX, paddingMixinWithSafeArea, isMobile(css(["", ""], mobilePaddingMixinWithSafeArea)));
SheetContent.displayName = 'SheetContent';
const SheetClose = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-4"
})(["position:relative;margin-inline-start:auto;margin-inline-end:0;display:flex;"]);
const StyledCloseButton = /*#__PURE__*/styled(Button).withConfig({
  componentId: "sc-storm-ui-40027066__sc-1rsnb3v-5"
})(["&&{color:", ";border:none;background-color:transparent;padding-block:", ";padding-inline:", ";min-height:auto;min-width:auto;", ";:hover,:active{color:", ";}:focus-visible{", ";outline-color:", ";}}"], ({
  theme
}) => theme.sheet.externalIcon.color.default, ({
  theme
}) => theme.controls.paddingBlock.iconBase.default, ({
  theme
}) => theme.controls.paddingBlock.iconBase.default, ({
  theme,
  $side
}) => {
  if ($side === 'start') {
    return css(["margin-block-start:", ";margin-block-end:auto;margin-inline-start:", ";margin-inline-end:auto;"], theme.spacing.small.default, theme.spacing.xsmall.default);
  }
  if ($side === 'end') {
    return css(["margin-block-start:", ";margin-block-end:auto;margin-inline-start:auto;margin-inline-end:", ";"], theme.spacing.small.default, theme.spacing.xsmall.default);
  }
  return css(["margin-block-start:auto;margin-block-end:", ";margin-inline-start:auto;margin-inline-end:", ";"], theme.spacing.xsmall.default, theme.spacing.small.default);
}, ({
  theme
}) => theme.sheet.externalIcon.color.hover, invertedFocusOutline, ({
  theme
}) => theme.sheet.externalIcon.outlineColor);

export { SheetClose as S, StyledCloseButton as a, Shroud as b, SheetContentContainer as c, SheetContentSurface as d, SheetContent as e, SHEET_CONTENT_Z_INDEX as f };
//# sourceMappingURL=Sheet.styles-DRUK9Jg4.js.map
